import React, { useCallback, useId, useMemo, useRef, useState } from "react";
import { Checkbox } from "Components/atoms/Checkbox";
import { Icon } from "Components/atoms/Icon";
import { Input as PriceInput } from "pages/Account/styles";
import { DestinationFieldSearch } from "Components/molecules/FieldSearch/Desktop/Destination";
import { OriginFieldSearch } from "Components/molecules/FieldSearch/Desktop/Origin";
import { AlertDateRangePicker } from "Components/molecules/AlertDateRangePicker";
import { useClickOutside } from "shared/hooks/useClickOutside";
import { useBreakpoint } from "shared/hooks/useBreakpoint";
import { getAirports } from "shared/services/contexts/airport.service";
import {
  CHANNEL_LABELS,
  ALERT_CHANNELS,
  centsToBRL,
  formatBRL,
  parsePriceInputToCents,
  toggleAlertChannel,
} from "shared/utils/alerts/alertFormat";
import type { AlertChannel } from "shared/services/alerts/alerts.types";
import {
  Actions,
  AirportField,
  BarChevron,
  BarContent,
  BarContainer,
  BarErrors,
  BarField,
  BarFieldsRow,
  BarIconWrapper,
  BarNotifyLabel,
  BarNotifyPopover,
  BarNotifyTrigger,
  BarNotifyValue,
  BarSearchSlot,
  NotifyConfirmButton,
  NotifyPopoverTitle,
  PricePopoverField,
  PricePopoverInput,
  PricePopoverPrefix,
  BarSubmitButton,
  ChannelList,
  ChannelOption,
  DatesCheckbox,
  DatesRow,
  ErrorSlot,
  FieldError,
  FieldGroup,
  FieldHint,
  FieldLabel,
  FormCard,
  FormFootnote,
  FormHeader,
  FormTitle,
  StatusBadge,
  VisuallyHidden,
  NotifyPopoverContent,
  SubmitButton,
} from "./styles";
import type { AlertFormProps, AlertFormValues } from "./types";
import type { Airport } from "shared/types/airportsTypes";

import { FIELD_ORDER, REQUIRED_MESSAGES, SUMMARY_LABELS } from "./validation";

/**
 * Rótulo curto de aeroporto para a barra: "Cidade (COD)".
 * A descrição completa ("Cidade - Aeroporto, País") não cabe no campo.
 * @param airport - Aeroporto selecionado (ou null).
 */
const getShortAirportLabel = (airport: Airport | null | undefined): string => {
  if (!airport?.description) return "";
  const city = airport.city || airport.description.split(" - ")[0];
  return airport.code ? `${city} (${airport.code})` : city;
};

/**
 * Formulário compartilhado de criação e edição de alertas de passagens.
 *
 * Suporta duas variantes de layout:
 * - "card" (padrão): layout vertical em card, mantido para compatibilidade.
 * - "bar": barra horizontal dentro do hero (desktop) ou empilhada (mobile).
 *
 * Criação e edição editam os mesmos campos (origem, destino, datas e preço) e
 * usam a mesma validação; `mode` só muda o texto do botão da barra.
 */
export const AlertForm: React.FC<AlertFormProps> = ({
  mode,
  initialValues,
  saving,
  userEmail,
  onSubmit,
  onValidationError,
  variant = "card",
  submitLabel,
  title,
  footnote,
  status,
}) => {
  const uid = useId();
  const breakpoint = useBreakpoint();
  const isMobile = !["lg", "xl", "xxl"].includes(breakpoint);

  const [values, setValues] = useState<AlertFormValues>(initialValues);
  const [errors, setErrors] = useState<
    Partial<Record<"origin" | "destination" | "departure" | "price", string>>
  >({});

  const [showOrigin, setShowOrigin] = useState(false);
  const [showDestination, setShowDestination] = useState(false);
  const [showNotify, setShowNotify] = useState(false);
  const [showPrice, setShowPrice] = useState(false);

  const originRef = useRef<HTMLDivElement>(null);
  const destinationRef = useRef<HTMLDivElement>(null);
  const notifyRef = useRef<HTMLDivElement>(null);
  const priceRef = useRef<HTMLDivElement>(null);

  const { origins, nationalDestinations, internationalDestinations } = useMemo(
    () => getAirports(),
    [],
  );

  useClickOutside([originRef], () => setShowOrigin(false));
  useClickOutside([destinationRef], () => setShowDestination(false));
  useClickOutside([notifyRef], () => setShowNotify(false));
  useClickOutside([priceRef], () => setShowPrice(false));

  const handleToggleChannel = (channel: AlertChannel) => {
    setValues((prev) => ({
      ...prev,
      channels: toggleAlertChannel(prev.channels, channel),
    }));
  };

  const channelsLabel = values.channels
    .map((channel) => CHANNEL_LABELS[channel])
    .join(", ");

  /**
   * Valida os valores de acordo com o modo.
   * Retorna um objeto com os campos inválidos e a mensagem de erro.
   */
  const validateForm = useCallback((): {
    missingFields: string[];
    message: string;
  } | null => {
    const missing: string[] = [];

    if (!values.origin) missing.push("origem");
    if (!values.destination) missing.push("destino");
    if (values.priceCents <= 0) missing.push("preço");
    if (!values.openDates && values.departureDates.length === 0)
      missing.push("data de ida");

    if (missing.length === 0) return null;

    const message =
      missing.length === 1
        ? `Preencha o campo obrigatório: ${missing[0]}.`
        : `Preencha os campos obrigatórios: ${missing.slice(0, -1).join(", ")} e ${missing[missing.length - 1]}.`;

    return { missingFields: missing, message };
  }, [values]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: typeof errors = {};
    if (!values.origin) newErrors.origin = REQUIRED_MESSAGES.origin;
    if (!values.destination)
      newErrors.destination = REQUIRED_MESSAGES.destination;
    if (values.priceCents <= 0) newErrors.price = REQUIRED_MESSAGES.price;
    if (!values.openDates && values.departureDates.length === 0)
      newErrors.departure = REQUIRED_MESSAGES.departure;
    setErrors(newErrors);

    const validation = validateForm();
    if (validation) {
      onValidationError?.(validation.missingFields, validation.message);
      return;
    }

    onSubmit(values);
  };

  const pendingLabels = FIELD_ORDER.filter((key) => errors[key]).map(
    (key) => SUMMARY_LABELS[key],
  );
  const pendingSummary =
    pendingLabels.length > 1
      ? `${pendingLabels.slice(0, -1).join(", ")} e ${pendingLabels[pendingLabels.length - 1]}`
      : pendingLabels[0];

  // Render card variant (default)
  if (variant === "card") {
    return (
      <FormCard onSubmit={handleSubmit} noValidate>
        {title && (
          <FormHeader>
            <FormTitle>{title}</FormTitle>
            {status && (
              <StatusBadge $paused={status === "paused"}>
                {status === "paused" ? "Alerta pausado" : "Alerta ativo"}
              </StatusBadge>
            )}
          </FormHeader>
        )}
        {/* Origem */}
        <FieldGroup role="group" aria-labelledby={`${uid}-origin`}>
          <VisuallyHidden id={`${uid}-origin`}>
            Partindo de onde?
          </VisuallyHidden>
          <AirportField ref={originRef}>
            <OriginFieldSearch
              origins={origins}
              origin={values.origin?.description || ""}
              isVisible={showOrigin}
              setIsVisible={setShowOrigin}
              onSelect={(airport: Airport) => {
                setValues((prev) => ({
                  ...prev,
                  origin: airport.code ? airport : null,
                }));
                setErrors((prev) => ({ ...prev, origin: undefined }));
                if (airport.code) setShowOrigin(false);
              }}
              fullWidthDropdown
            />
          </AirportField>
          <ErrorSlot>
            {errors.origin && <FieldError>{errors.origin}</FieldError>}
          </ErrorSlot>
        </FieldGroup>

        {/* Destino */}
        <FieldGroup role="group" aria-labelledby={`${uid}-destination`}>
          <VisuallyHidden id={`${uid}-destination`}>
            Indo para onde?
          </VisuallyHidden>
          <AirportField ref={destinationRef}>
            <DestinationFieldSearch
              nationalDestinations={nationalDestinations}
              internationalDestinations={internationalDestinations}
              destination={values.destination?.description || ""}
              isVisible={showDestination}
              setIsVisible={setShowDestination}
              onSelect={(airport: Airport) => {
                setValues((prev) => ({
                  ...prev,
                  destination: airport.code ? airport : null,
                }));
                setErrors((prev) => ({ ...prev, destination: undefined }));
                if (airport.code) setShowDestination(false);
              }}
              fullWidthDropdown
            />
          </AirportField>
          <ErrorSlot>
            {errors.destination && (
              <FieldError>{errors.destination}</FieldError>
            )}
          </ErrorSlot>
        </FieldGroup>

        {/* Datas Abertas Checkbox */}
        <FieldGroup>
          <Checkbox
            name="open-dates"
            value="open-dates"
            isChecked={values.openDates}
            label="Alerta por preço independente de datas"
            onChange={() => {
              setValues((prev) => ({
                ...prev,
                openDates: !prev.openDates,
                departureDates: !prev.openDates ? [] : prev.departureDates,
                returnDates: !prev.openDates ? [] : prev.returnDates,
              }));
              setErrors((prev) => ({ ...prev, departure: undefined }));
            }}
          />
        </FieldGroup>

        {/* Dates Row */}
        <DatesRow>
          {/* Data de Ida */}
          <FieldGroup role="group" aria-labelledby={`${uid}-departure`}>
            <FieldLabel id={`${uid}-departure`}>Ida</FieldLabel>
            <AlertDateRangePicker
              label="Ida"
              popoverAlign="start"
              placeholder="Escolher datas"
              value={values.departureDates}
              onChange={(dates: string[]) => {
                setValues((prev) => ({
                  ...prev,
                  departureDates: dates,
                  returnDates:
                    dates.length > 0 &&
                    prev.returnDates.length > 0 &&
                    dates[0] > prev.returnDates[0]
                      ? []
                      : prev.returnDates,
                }));
                setErrors((prev) => ({ ...prev, departure: undefined }));
              }}
              disabled={values.openDates}
              isMobile={isMobile}
            />
          </FieldGroup>

          {/* Data de Volta */}
          <FieldGroup role="group" aria-labelledby={`${uid}-return`}>
            <FieldLabel id={`${uid}-return`}>Volta (opcional)</FieldLabel>
            <AlertDateRangePicker
              label="Volta"
              popoverAlign="end"
              placeholder={
                !values.openDates && values.departureDates.length === 0
                  ? "Escolha a ida primeiro"
                  : "Escolher datas"
              }
              value={values.returnDates}
              onChange={(dates: string[]) => {
                setValues((prev) => ({
                  ...prev,
                  returnDates: dates,
                }));
              }}
              minDate={
                values.departureDates.length > 0
                  ? values.departureDates[0]
                  : undefined
              }
              disabled={values.openDates || values.departureDates.length === 0}
              isMobile={isMobile}
            />
          </FieldGroup>
          <ErrorSlot>
            {errors.departure && <FieldError>{errors.departure}</FieldError>}
          </ErrorSlot>
        </DatesRow>

        {/* Preço Máximo */}
        <FieldGroup role="group" aria-labelledby={`${uid}-price`}>
          <FieldLabel id={`${uid}-price`}>Preço máximo por pessoa</FieldLabel>
          <PriceInput
            type="text"
            inputMode="numeric"
            autoComplete="off"
            placeholder="R$ 0,00"
            value={
              values.priceCents ? formatBRL(centsToBRL(values.priceCents)) : ""
            }
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setValues((prev) => ({
                ...prev,
                priceCents: parsePriceInputToCents(e.target.value),
              }));
              setErrors((prev) => ({ ...prev, price: undefined }));
            }}
            $hasError={Boolean(errors.price)}
            aria-invalid={Boolean(errors.price)}
          />
          <ErrorSlot>
            {errors.price && <FieldError>{errors.price}</FieldError>}
          </ErrorSlot>
        </FieldGroup>

        {/* Notification Channel */}
        <FieldGroup role="group" aria-labelledby={`${uid}-channels`}>
          <FieldLabel id={`${uid}-channels`}>Me notifique por:</FieldLabel>
          <ChannelList>
            {/* Email Channel */}
            <ChannelOption>
              <Checkbox
                name="alert-channel"
                value="email"
                isChecked={values.channels.includes("email")}
                label="E-mail"
                onChange={() => handleToggleChannel("email")}
              />
              {values.channels.includes("email") && (
                <FieldHint>Enviaremos para {userEmail}.</FieldHint>
              )}
            </ChannelOption>

            {/* Push Channel */}
            <ChannelOption>
              <Checkbox
                name="alert-channel"
                value="push"
                isChecked={values.channels.includes("push")}
                label="Push"
                onChange={() => handleToggleChannel("push")}
              />
              {values.channels.includes("push") && (
                <FieldHint>
                  Notificação no app Voopter. É preciso ter o app instalado e
                  estar logado nele.
                </FieldHint>
              )}
            </ChannelOption>
          </ChannelList>
        </FieldGroup>

        {/* Submit Button */}
        <Actions>
          <SubmitButton type="submit" disabled={saving}>
            {saving ? "Salvando..." : (submitLabel ?? "Salvar alerta")}
          </SubmitButton>
        </Actions>
        {footnote && <FormFootnote>{footnote}</FormFootnote>}
      </FormCard>
    );
  }

  // Render bar variant
  return (
    <BarContainer onSubmit={handleSubmit} noValidate>
      <BarContent>
        {/* Main bar row with fields */}
        <BarFieldsRow>
          {/* Origem */}
          <BarField
            ref={originRef}
            $hasError={Boolean(errors.origin)}
            $flex="1.7"
          >
            <BarSearchSlot>
              <OriginFieldSearch
                origins={origins}
                origin={getShortAirportLabel(values.origin)}
                isVisible={showOrigin}
                setIsVisible={setShowOrigin}
                onSelect={(airport: Airport) => {
                  setValues((prev) => ({
                    ...prev,
                    origin: airport.code ? airport : null,
                  }));
                  setErrors((prev) => ({ ...prev, origin: undefined }));
                  if (airport.code) setShowOrigin(false);
                }}
                variant="bar"
              />
            </BarSearchSlot>
          </BarField>

          {/* Destino */}
          <BarField
            ref={destinationRef}
            $hasError={Boolean(errors.destination)}
            $flex="1.7"
          >
            <BarSearchSlot>
              <DestinationFieldSearch
                nationalDestinations={nationalDestinations}
                internationalDestinations={internationalDestinations}
                destination={getShortAirportLabel(values.destination)}
                isVisible={showDestination}
                setIsVisible={setShowDestination}
                onSelect={(airport: Airport) => {
                  setValues((prev) => ({
                    ...prev,
                    destination: airport.code ? airport : null,
                  }));
                  setErrors((prev) => ({
                    ...prev,
                    destination: undefined,
                  }));
                  if (airport.code) setShowDestination(false);
                }}
                variant="bar"
              />
            </BarSearchSlot>
          </BarField>

          {/* Data de Ida */}
          <BarField
            $disabled={values.openDates}
            $hasError={Boolean(errors.departure)}
            $flex="0 0 12rem"
          >
            <BarIconWrapper>
              <Icon
                library="lu"
                name="LuCalendar"
                size="medium"
                label="Data de ida"
              />
            </BarIconWrapper>
            <div style={{ flex: 1, minWidth: 0 }}>
              <AlertDateRangePicker
                label="Ida"
                placeholder="Datas de ida"
                value={values.departureDates}
                onChange={(dates: string[]) => {
                  setValues((prev) => ({
                    ...prev,
                    departureDates: dates,
                    returnDates:
                      dates.length > 0 &&
                      prev.returnDates.length > 0 &&
                      dates[0] > prev.returnDates[0]
                        ? []
                        : prev.returnDates,
                  }));
                  setErrors((prev) => ({ ...prev, departure: undefined }));
                }}
                disabled={values.openDates}
                isMobile={isMobile}
                variant="bar"
              />
            </div>
          </BarField>

          {/* Data de Volta */}
          <BarField
            $disabled={values.openDates || values.departureDates.length === 0}
            $flex="0 0 12rem"
          >
            <BarIconWrapper>
              <Icon
                library="lu"
                name="LuCalendar"
                size="medium"
                label="Data de volta"
              />
            </BarIconWrapper>
            <div style={{ flex: 1, minWidth: 0 }}>
              <AlertDateRangePicker
                label="Volta"
                placeholder="Datas de volta"
                value={values.returnDates}
                onChange={(dates: string[]) => {
                  setValues((prev) => ({
                    ...prev,
                    returnDates: dates,
                  }));
                }}
                minDate={
                  values.departureDates.length > 0
                    ? values.departureDates[0]
                    : undefined
                }
                disabled={
                  values.openDates || values.departureDates.length === 0
                }
                isMobile={isMobile}
                variant="bar"
              />
            </div>
          </BarField>

          {/* Preço */}
          <BarField
            ref={priceRef}
            $hasError={Boolean(errors.price)}
            $flex="0 0 11.5rem"
          >
            <BarIconWrapper>
              <Icon
                library="lu"
                name="LuHandCoins"
                size="medium"
                label="Preço máximo"
              />
            </BarIconWrapper>
            <BarNotifyTrigger
              onClick={() => setShowPrice(!showPrice)}
              type="button"
              aria-invalid={Boolean(errors.price)}
            >
              {values.priceCents ? (
                <BarNotifyValue>
                  {formatBRL(centsToBRL(values.priceCents))}
                </BarNotifyValue>
              ) : (
                <BarNotifyLabel>Preço</BarNotifyLabel>
              )}
            </BarNotifyTrigger>

            {showPrice && (
              <BarNotifyPopover>
                <NotifyPopoverContent>
                  <NotifyPopoverTitle>
                    Coloque o preço desejado:
                  </NotifyPopoverTitle>
                  <PricePopoverField>
                    <PricePopoverPrefix>R$</PricePopoverPrefix>
                    <PricePopoverInput
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      aria-label="Preço máximo"
                      aria-invalid={Boolean(errors.price)}
                      // biome-ignore lint/a11y/noAutofocus: popover abre por ação do usuário
                      autoFocus
                      value={
                        values.priceCents
                          ? formatBRL(centsToBRL(values.priceCents)).replace(
                              /^R\$\s*/,
                              "",
                            )
                          : ""
                      }
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setValues((prev) => ({
                          ...prev,
                          priceCents: parsePriceInputToCents(e.target.value),
                        }));
                        setErrors((prev) => ({ ...prev, price: undefined }));
                      }}
                      onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          setShowPrice(false);
                        }
                      }}
                    />
                  </PricePopoverField>
                  <NotifyConfirmButton
                    type="button"
                    aria-label="Confirmar preço"
                    onClick={() => setShowPrice(false)}
                  >
                    <Icon
                      library="lu"
                      name="LuCheck"
                      size="medium"
                      label="Confirmar"
                    />
                  </NotifyConfirmButton>
                </NotifyPopoverContent>
              </BarNotifyPopover>
            )}
          </BarField>

          {/* Notificação */}
          <BarField ref={notifyRef} $flex="0 0 12rem">
            <BarIconWrapper>
              <Icon
                library="lu"
                name="LuBell"
                size="medium"
                label="Canal de notificação"
              />
            </BarIconWrapper>
            <BarNotifyTrigger
              onClick={() => setShowNotify(!showNotify)}
              type="button"
              aria-label={`Me notifique por: ${channelsLabel}`}
            >
              <BarNotifyValue>{channelsLabel}</BarNotifyValue>
            </BarNotifyTrigger>
            <BarChevron>⌄</BarChevron>

            {showNotify && (
              <BarNotifyPopover>
                <NotifyPopoverContent>
                  <NotifyPopoverTitle>
                    Por onde deseja receber a notificação?
                  </NotifyPopoverTitle>
                  {ALERT_CHANNELS.map((channel) => (
                    <ChannelOption key={channel}>
                      <Checkbox
                        name="alert-channel"
                        value={channel}
                        isChecked={values.channels.includes(channel)}
                        label={CHANNEL_LABELS[channel]}
                        onChange={() => handleToggleChannel(channel)}
                      />
                    </ChannelOption>
                  ))}
                  <NotifyConfirmButton
                    type="button"
                    aria-label="Confirmar canal de notificação"
                    onClick={() => setShowNotify(false)}
                  >
                    <Icon
                      library="lu"
                      name="LuCheck"
                      size="medium"
                      label="Confirmar"
                    />
                  </NotifyConfirmButton>
                </NotifyPopoverContent>
              </BarNotifyPopover>
            )}
          </BarField>
        </BarFieldsRow>

        {/* Alerta por preço independente de datas (datas abertas) */}
        <DatesCheckbox>
          <Checkbox
            name="open-dates-bar"
            value="open-dates-bar"
            isChecked={values.openDates}
            label="Alerta por preço independente de datas"
            onChange={() => {
              setValues((prev) => ({
                ...prev,
                openDates: !prev.openDates,
                departureDates: !prev.openDates ? [] : prev.departureDates,
                returnDates: !prev.openDates ? [] : prev.returnDates,
              }));
              setErrors((prev) => ({ ...prev, departure: undefined }));
            }}
          />
        </DatesCheckbox>

        {/* Inline errors */}
        {pendingSummary && (
          <BarErrors role="alert">Falta preencher: {pendingSummary}.</BarErrors>
        )}
      </BarContent>

      {/* Submit Button - on same row as bar (desktop), below content (mobile) */}
      <BarSubmitButton type="submit" disabled={saving}>
        {saving
          ? "Salvando..."
          : mode === "create"
            ? "＋ Novo alerta"
            : "Salvar alerta"}
      </BarSubmitButton>
    </BarContainer>
  );
};
